/**
 * The one balance connection of the app. It outlives pages (connect once at the bench, keep weighing in
 * receiving or stocktake), combines the balance's stability flag with the software window, and survives a
 * USB unplug: the status becomes "reconnecting" and the remembered port reopens when it comes back. An open
 * port says nothing about the balance behind it, so a balance that was asked for data and sends nothing
 * readable within a few seconds gets `noData` and an error saying what to check.
 */
import type { DecimalString } from "@/lib/decimal";
import { createKeyboardWedgeTransport } from "./keyboardWedge";
import { PROTOCOLS } from "./protocols";
import {
  createSerialTransport,
  findRememberedPort,
  forgetSessionPort,
  pickSerialPort,
  rememberedPort,
  rememberPort,
  rememberedSessionPort,
  rememberSessionPort,
  serialSupport,
  watchSerialReconnect,
  type SerialPortLike,
  type RememberedPort,
} from "./serialTransport";
import { createSimulatorTransport, SimulatedBalance, type SimulatorTransport } from "./simulator";
import { StabilityDetector } from "./stability";
import type {
  BalanceProtocol,
  BalanceReading,
  BalanceTransport,
  ProtocolId,
  SerialSettings,
  TransportKind,
} from "./types";

export type BalanceStatus = "disconnected" | "connecting" | "connected" | "reconnecting" | "error";

/** Why a connected balance has given no reading: it sent nothing at all, or only data the protocol can't read. */
export type NoData = "silent" | "unreadable";

export interface BalanceIdentity {
  model?: string;
  serial?: string;
  id?: string;
}

export interface BalanceSnapshot {
  status: BalanceStatus;
  transport: TransportKind | null;
  protocol: ProtocolId;
  reading: BalanceReading | null;
  /** The balance's flag and the software window agree (keyboard readings count as stable). */
  stable: boolean;
  identity: BalanceIdentity;
  /** The last problem, in words the user can act on; cleared on the next connection. */
  error: string | null;
  /** Set when the balance sent nothing readable in its first seconds; cleared by the first line it understands. */
  noData: NoData | null;
  /** Readability used by the stability window. */
  resolution: DecimalString;
  canWrite: boolean;
}

export interface ConnectOptions {
  protocol?: ProtocolId;
  /** A port already chosen (reconnects); otherwise the browser's picker opens. */
  port?: SerialPortLike;
  simulator?: SimulatedBalance;
}

export type ReadingListener = (reading: BalanceReading, stable: boolean) => void;

const INITIAL: BalanceSnapshot = {
  status: "disconnected",
  transport: null,
  protocol: "and",
  reading: null,
  stable: false,
  identity: {},
  error: null,
  noData: null,
  resolution: "0.001",
  canWrite: false,
};

/** How long a balance that was asked to identify itself or stream may take to send a line the app can read. */
export const ANSWER_TIMEOUT_MS = 3000;
const CAPTURE_POLL_MS = 250;
const CAPTURE_TIMEOUT_MS = 15_000;
/** A reload can race the old document releasing the operating system's serial handle. */
const RECONNECT_DELAYS = [0, 250, 750, 1500];

function describe(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "The balance stopped responding";
}

function describeSettings({ baudRate, dataBits, parity }: SerialSettings): string {
  return `${baudRate} baud, ${dataBits} data bits, ${parity === "none" ? "no" : parity} parity`;
}

function noDataMessage(noData: NoData, protocol: BalanceProtocol): string {
  const settings = describeSettings(protocol.serial);
  return noData === "silent"
    ? `The balance isn't answering. Check that it's switched on, that its cable or USB adapter is wired straight through (a null-modem one connects but carries nothing) and that the balance is set to ${settings}.`
    : `The balance is sending data the app can't read. Set it to ${settings}, or disconnect and choose the format it speaks in the balance menu.`;
}

function isPickerCancelled(error: unknown): boolean {
  return error instanceof DOMException && (error.name === "NotFoundError" || error.name === "AbortError");
}

export class BalanceStore {
  private snapshot: BalanceSnapshot = INITIAL;
  private readonly listeners = new Set<() => void>();
  private readonly readingListeners = new Set<ReadingListener>();
  private transport: BalanceTransport | null = null;
  private protocol: BalanceProtocol = PROTOCOLS.and;
  private readonly detector = new StabilityDetector(INITIAL.resolution);
  private stopWatching: (() => void) | null = null;
  private connection = 0;
  /** User intent changes immediately, even while a picker, permission lookup or port open is pending. */
  private operation = 0;
  private transitions: Promise<void> = Promise.resolve();
  private restored: Promise<boolean> | null = null;
  private recovering: Promise<boolean> | null = null;
  private answerTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingCapture: { promise: Promise<BalanceReading>; cancel: (error: Error) => void } | null = null;
  /** Bytes arrived on this connection, readable or not. */
  private heard = false;
  /** A line on this connection parsed into a reading, identity, ACK or error. */
  private answered = false;
  private readonly now: () => number;
  /** The simulated balance while the simulator is connected (for demo controls and tests). */
  simulator: SimulatedBalance | null = null;

  constructor(now: () => number = () => performance.now()) {
    this.now = now;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): BalanceSnapshot => this.snapshot;

  /** Every reading as it arrives (the snapshot only keeps the latest). */
  onReading = (listener: ReadingListener): (() => void) => {
    this.readingListeners.add(listener);
    return () => this.readingListeners.delete(listener);
  };

  connect = async (kind: TransportKind, options: ConnectOptions = {}): Promise<void> => {
    const operation = this.beginOperation();
    let port = options.port ?? null;
    if (kind !== "serial") forgetSessionPort();
    if (kind === "serial" && !port) {
      try {
        // Keep the browser picker in the click's user activation, before any other await.
        port = await pickSerialPort();
      } catch (error) {
        if (operation === this.operation) {
          if (!isPickerCancelled(error)) this.update({ status: "error", error: describe(error) });
          else if (!this.transport) {
            forgetSessionPort();
            this.disconnectedSnapshot();
          }
          const remembered = rememberedSessionPort();
          if (this.transport && remembered) this.watch(remembered);
        }
        return;
      }
    }
    if (operation !== this.operation) return;
    const protocolId: ProtocolId =
      options.protocol ??
      (kind === "keyboard" ? "generic" : kind === "serial" ? (rememberedPort()?.protocol ?? "and") : "and");
    const protocol = PROTOCOLS[protocolId];
    let transport: BalanceTransport;
    if (kind === "serial" && port) transport = createSerialTransport(port, protocol.serial);
    else if (kind === "keyboard") transport = createKeyboardWedgeTransport();
    else transport = createSimulatorTransport(options.simulator ?? new SimulatedBalance());
    await this.transition(async () => {
      try {
        await this.openTransport(transport, protocolId, operation, false, () => {
          if (kind === "serial" && port) this.rememberConnection(port, protocolId);
        });
      } catch (error) {
        if (operation === this.operation)
          this.update({ status: "error", transport: null, canWrite: false, error: describe(error) });
      }
    });
  };

  /** Connects any transport (tests and custom devices use this directly). */
  connectTransport = async (transport: BalanceTransport, protocolId: ProtocolId): Promise<void> => {
    const operation = this.beginOperation();
    forgetSessionPort();
    await this.transition(async () => {
      try {
        await this.openTransport(transport, protocolId, operation);
      } catch (error) {
        if (operation === this.operation)
          this.update({ status: "error", transport: null, canWrite: false, error: describe(error) });
      }
    });
  };

  private async openTransport(
    transport: BalanceTransport,
    protocolId: ProtocolId,
    operation: number,
    automatic = false,
    onConnected?: () => void,
  ): Promise<boolean> {
    if (operation !== this.operation) return false;
    await this.closeTransport();
    if (operation !== this.operation) return false;
    const connection = ++this.connection;
    this.protocol = PROTOCOLS[protocolId];
    this.simulator = transport.kind === "simulator" ? (transport as SimulatorTransport).simulator : null;
    this.detector.reset();
    this.heard = false;
    this.answered = false;
    this.update({
      status: automatic ? "reconnecting" : "connecting",
      transport: transport.kind,
      protocol: protocolId,
      reading: null,
      stable: false,
      identity: {},
      error: null,
      noData: null,
      canWrite: transport.canWrite,
    });
    let ended = false;
    await transport.open({
      onLine: (line) => {
        if (connection === this.connection) this.handleLine(line);
      },
      onActivity: () => {
        if (connection === this.connection) this.heard = true;
      },
      onClose: (error) => {
        ended = true;
        if (connection === this.connection) this.handleClose(error);
      },
    });
    if (operation !== this.operation || ended) {
      await transport.close().catch(() => undefined);
      return false;
    }
    this.transport = transport;
    this.update({ status: "connected" });
    onConnected?.();
    if (transport.canWrite) {
      const commands = [...this.protocol.commands.identify];
      if (this.protocol.commands.startStream) commands.push(this.protocol.commands.startStream);
      for (const command of commands) {
        if (operation !== this.operation || connection !== this.connection) return false;
        await this.send(command);
      }
      if (operation === this.operation && connection === this.connection) this.expectAnswer(connection);
    }
    return operation === this.operation && this.snapshot.status === "connected";
  }

  disconnect = async (): Promise<void> => {
    const operation = this.beginOperation();
    this.connection += 1;
    forgetSessionPort();
    this.restored = Promise.resolve(false);
    this.disconnectedSnapshot();
    await this.transition(async () => {
      if (operation === this.operation) await this.closeTransport();
    });
  };

  /** Release the document's port on pagehide, retaining only this tab's reconnect intent. */
  suspendConnection = async (): Promise<void> => {
    const operation = this.beginOperation();
    this.connection += 1;
    this.restored = null;
    this.disconnectedSnapshot();
    await this.transition(async () => {
      if (operation === this.operation) await this.closeTransport(false);
    });
  };

  /** Idempotent startup/BFCache restoration. It never opens a permission picker. */
  restoreConnection = (): Promise<boolean> => {
    if (this.restored) return this.restored;
    if (this.snapshot.status === "connected" || this.snapshot.status === "connecting")
      return Promise.resolve(this.snapshot.status === "connected");
    const remembered = rememberedSessionPort();
    if (!remembered || !serialSupport().supported) return (this.restored = Promise.resolve(false));
    this.restored = this.recover(remembered);
    return this.restored;
  };

  /** Explicit retry can use the global last choice, but startup restores only this tab's choice. */
  reconnectRemembered = (): Promise<boolean> => {
    const remembered = rememberedSessionPort() ?? rememberedPort();
    if (!remembered || !serialSupport().supported) return Promise.resolve(false);
    return this.recover(remembered);
  };

  private recover(remembered: RememberedPort): Promise<boolean> {
    const operation = this.beginOperation();
    this.watch(remembered);
    this.update({
      status: "reconnecting",
      transport: "serial",
      protocol: remembered.protocol,
      reading: null,
      stable: false,
      noData: null,
      canWrite: false,
      error: null,
    });
    const recovery = (async () => {
      try {
        for (const delay of RECONNECT_DELAYS) {
          if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
          if (operation !== this.operation) return false;
          const port = await findRememberedPort(remembered);
          if (operation !== this.operation) return false;
          if (!port) {
            this.update({
              status: "reconnecting",
              error:
                "The saved balance is not available. Plug it in, or choose it again from the USB balance menu if browser permission was removed.",
            });
            return false;
          }
          try {
            let connected = false;
            await this.transition(async () => {
              connected = await this.openTransport(
                createSerialTransport(port, PROTOCOLS[remembered.protocol].serial),
                remembered.protocol,
                operation,
                true,
                () => this.rememberConnection(port, remembered.protocol),
              );
            });
            if (connected) return true;
            if (operation !== this.operation) return false;
          } catch (error) {
            const transient =
              error instanceof DOMException && ["NetworkError", "InvalidStateError"].includes(error.name);
            if (!transient || delay === RECONNECT_DELAYS.at(-1)) throw error;
          }
        }
      } catch (error) {
        if (operation === this.operation)
          this.update({
            status: "error",
            transport: null,
            canWrite: false,
            error: `Could not reconnect the balance. ${describe(error)} Close other tabs or apps using it, then retry or choose the USB balance again.`,
          });
      }
      return false;
    })();
    this.recovering = recovery;
    void recovery.finally(() => {
      if (this.recovering === recovery) this.recovering = null;
    });
    return recovery;
  }

  private rememberConnection(port: SerialPortLike, protocol: ProtocolId): void {
    rememberPort(port, protocol);
    rememberSessionPort(port, protocol);
    this.watch({ ...port.getInfo(), protocol });
  }

  private watch(remembered: RememberedPort): void {
    this.stopWatching?.();
    this.stopWatching = watchSerialReconnect(() => {
      if (this.snapshot.status === "reconnecting" && !this.recovering) void this.recover(remembered);
    }, remembered);
  }

  private beginOperation(): number {
    this.pendingCapture?.cancel(new Error("The balance connection changed. Read the weight again after reconnecting."));
    this.stopWatching?.();
    this.stopWatching = null;
    this.recovering = null;
    this.clearAnswerTimer();
    return ++this.operation;
  }

  private transition(action: () => Promise<void>): Promise<void> {
    const next = this.transitions.then(action, action);
    this.transitions = next.catch(() => undefined);
    return next;
  }

  private disconnectedSnapshot(): void {
    this.update({
      status: "disconnected",
      transport: null,
      reading: null,
      stable: false,
      noData: null,
      canWrite: false,
      error: null,
    });
  }

  send = async (command: string): Promise<void> => {
    const transport = this.transport;
    if (!transport?.canWrite) return;
    const connection = this.connection;
    try {
      await transport.write(command + this.protocol.lineEnding);
    } catch (error) {
      if (connection === this.connection) this.update({ error: describe(error) });
    }
  };

  tare = (): Promise<void> => this.send(this.protocol.commands.tare ?? "T");

  zero = (): Promise<void> => this.send(this.protocol.commands.zero ?? "Z");

  requestStable = (): Promise<void> => this.send(this.protocol.commands.stable ?? "S");

  /** A new measurement only: request Q/SI until fresh samples cover a stable window, never reuse the cache. */
  captureStableReading = (timeoutMs = CAPTURE_TIMEOUT_MS): Promise<BalanceReading> => {
    if (this.pendingCapture) return this.pendingCapture.promise;
    const transport = this.transport;
    if (this.snapshot.status !== "connected" || !transport)
      return Promise.reject(new Error("The balance isn't connected"));
    const connection = this.connection;
    const detector = new StabilityDetector(this.snapshot.resolution);
    const command = transport.canWrite ? this.protocol.commands.immediate : undefined;
    const lineEnding = this.protocol.lineEnding;
    let resolve!: (reading: BalanceReading) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<BalanceReading>((done, fail) => {
      resolve = done;
      reject = fail;
    });
    let finished = false;
    let received = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let stopReading: () => void = () => undefined;
    let stopStatus: () => void = () => undefined;
    const finish = (error: Error | null, reading?: BalanceReading) => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      if (pollTimer !== null) clearTimeout(pollTimer);
      stopReading();
      stopStatus();
      if (this.pendingCapture?.promise === promise) this.pendingCapture = null;
      if (error) reject(error);
      else if (reading) resolve(reading);
    };
    const timeout = setTimeout(() => {
      const message = received
        ? "The fresh weight didn't settle. Check for drafts and vibration, then try again."
        : transport.kind === "keyboard"
          ? "No new weight arrived. Press PRINT on the balance, or use a two-way serial connection for automatic reads."
          : command
            ? "The balance didn't answer the fresh-weight request. Check its connection and serial settings, then try again."
            : "No new weight arrived. Send a reading from the balance, then try again.";
      finish(new Error(message));
    }, timeoutMs);
    this.pendingCapture = { promise, cancel: (error) => finish(error) };
    stopReading = this.onReading((reading) => {
      received = true;
      const stable =
        transport.kind === "keyboard"
          ? reading.grams !== null && !reading.overload && reading.stable !== false
          : detector.push(reading);
      if (stable && reading.grams !== null) finish(null, reading);
    });
    stopStatus = this.subscribe(() => {
      if (connection !== this.connection || this.snapshot.status !== "connected")
        finish(new Error("The balance disconnected before sending a fresh weight. Reconnect and try again."));
    });
    // Subscribe before writing: the simulator and some adapters can reply synchronously. Scheduling
    // after each completed write also keeps slow adapters from accumulating a queue of Q commands.
    const request = async () => {
      if (finished || !command) return;
      try {
        await transport.write(command + lineEnding);
      } catch (error) {
        finish(new Error(`Could not request a fresh weight. ${describe(error)}`));
        return;
      }
      if (!finished) pollTimer = setTimeout(() => void request(), CAPTURE_POLL_MS);
    };
    void request();
    return promise;
  };

  identify = async (): Promise<void> => {
    for (const command of this.protocol.commands.identify) await this.send(command);
  };

  setResolution = (resolution: DecimalString): void => {
    this.detector.setResolution(resolution);
    this.update({ resolution });
  };

  /** Without a line it can read within ANSWER_TIMEOUT_MS of asking, the balance gets `noData`. */
  private expectAnswer(connection: number): void {
    const { identify, startStream } = this.protocol.commands;
    // Generic balances are never asked for anything, so a quiet one may just be waiting for PRINT.
    if (this.answered || (!identify.length && !startStream)) return;
    this.clearAnswerTimer();
    this.answerTimer = setTimeout(() => {
      this.answerTimer = null;
      if (connection !== this.connection || this.answered) return;
      const noData: NoData = this.heard ? "unreadable" : "silent";
      this.update({ noData, error: noDataMessage(noData, this.protocol) });
    }, ANSWER_TIMEOUT_MS);
  }

  private clearAnswerTimer(): void {
    if (this.answerTimer !== null) clearTimeout(this.answerTimer);
    this.answerTimer = null;
  }

  private async closeTransport(stopStream = true): Promise<void> {
    this.clearAnswerTimer();
    const transport = this.transport;
    this.connection += 1;
    this.transport = null;
    this.simulator = null;
    if (!transport) return;
    const stop = this.protocol.commands.stopStream;
    if (stopStream && transport.canWrite && stop)
      await transport.write(stop + this.protocol.lineEnding).catch(() => undefined);
    await transport.close().catch(() => undefined);
  }

  private handleLine(line: string): void {
    this.heard = true;
    const event = this.protocol.parse(line, this.now());
    if (!event) return;
    if (!this.answered) {
      this.answered = true;
      this.clearAnswerTimer();
      if (this.snapshot.noData) this.update({ noData: null, error: null });
    }
    if (event.type === "reading") {
      const { reading } = event;
      const stable =
        this.snapshot.transport === "keyboard"
          ? reading.grams !== null && !reading.overload && reading.stable !== false
          : this.detector.push(reading);
      this.update({ reading, stable });
      for (const listener of this.readingListeners) listener(reading, stable);
    } else if (event.type === "identity") {
      this.update({ identity: { ...this.snapshot.identity, [event.field]: event.value } });
    } else if (event.type === "error") {
      this.pendingCapture?.cancel(new Error(event.message));
      this.update({ error: event.message });
    }
  }

  private handleClose(error?: Error): void {
    this.pendingCapture?.cancel(
      new Error("The balance disconnected before sending a fresh weight. Reconnect and try again."),
    );
    this.clearAnswerTimer();
    this.transport = null;
    this.simulator = null;
    this.detector.reset();
    if (error && this.snapshot.transport === "serial") {
      this.update({
        status: "reconnecting",
        reading: null,
        stable: false,
        noData: null,
        canWrite: false,
        error: "The balance was disconnected. Plug it back in to carry on; recorded lines are saved.",
      });
      return;
    }
    this.update({
      status: error ? "error" : "disconnected",
      transport: null,
      reading: null,
      stable: false,
      noData: null,
      canWrite: false,
      error: error ? describe(error) : null,
    });
  }

  private update(changes: Partial<BalanceSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...changes };
    for (const listener of this.listeners) listener();
  }
}

export const balanceStore = new BalanceStore();
